import { RegisterScreen } from '@/components/RegisterScreen';
import { StatusPill, formatDate, formatNumber, todayIso } from '@/components/live';
import { useRows } from '@/hooks/useResource';
import {
  dateNotFuture, plotCell, plotField, speciesBelongsToPlot, speciesField, usePracticePickers,
} from '@/screens/parts/practiceFields';
import {
  ISSUE_TYPES, PEST_ACTIONS, PEST_STATUS, PLANT_PARTS, SAMPLES, SEVERITIES, optionLabel,
} from '@/lib/practiceLists';

interface Row {
  id: string; pest_disease_code: string | null; plot_code: string | null; farmer_name: string | null; village_name: string | null;
  species_label: string | null; observed_on: string; issue_type: string; issue_name: string; plant_part: string | null;
  severity: string; incidence_pct: number | null; trees_affected: number | null; action_taken: string;
  treatment_product: string | null; record_status: string; follow_up_on: string | null;
}

/**
 * Every pest, disease and other damage observed (owner, 2026-09-25: "these all make as ui pages
 * under Plot management").
 *
 * A pest is the one thing in these six registers that does not stay on its plot. Read plot by
 * plot, an outbreak is a handful of unremarkable single observations; read by village and issue
 * name, it is a pattern - which is the point of gathering them in one register. The follow-ups
 * that have come due are here too, because an observation with a date nobody returns to is just
 * a note.
 */
export default function PestsScreen() {
  const pick = usePracticePickers();
  const today = todayIso();
  // Only rows still open: a follow-up date on a resolved record is history, not a task.
  const overdue = (row: Row) => Boolean(row.follow_up_on) && row.follow_up_on! <= today && row.record_status === 'open';
  const inputs = useRows<{ id: string; product_name: string; applied_on: string; plot_id: string }>('plot_input_applications', {});

  return (
    <RegisterScreen<Row>
      plotFilter
      addLabel="Add observation" exportName="pests-and-diseases"
      orderBy={{ column: 'observed_on', ascending: false }}
      recordLabel={(row) => `${row.issue_name} on ${row.plot_code ?? ''}`}
      screenKey="plot.inventory"
      searchColumns={['pest_disease_code', 'plot_code', 'farmer_name', 'issue_name', 'species_label', 'symptoms']}
      subtitle="Pests, diseases, weeds and damage - what was seen, how bad, what was done and what is still open."
      table="ops.plot_pest_disease_records" title="Pests & diseases" view="plot_pest_disease_records"
      columns={[
        { key: 'plot_code', label: 'Plot', render: plotCell },
        { key: 'village_name', label: 'Village', render: (row) => row.village_name ?? '--' },
        { key: 'issue_name', label: 'Issue', render: (row) => (
          <span className="cell-stack">
            <strong>{row.issue_name}</strong>
            <small className="muted">{[optionLabel(ISSUE_TYPES, row.issue_type), row.species_label, row.plant_part ? optionLabel(PLANT_PARTS, row.plant_part) : null].filter(Boolean).join(' · ')}</small>
          </span>
        ) },
        { key: 'observed_on', label: 'Observed', render: (row) => formatDate(row.observed_on) },
        { key: 'severity', label: 'Severity', render: (row) => (
          ['high', 'severe'].includes(row.severity)
            ? <span className="field-error">{optionLabel(SEVERITIES, row.severity)}</span>
            : optionLabel(SEVERITIES, row.severity)
        ) },
        { key: 'incidence_pct', label: 'Incidence', align: 'right',
          render: (row) => (row.incidence_pct == null ? '--' : `${formatNumber(row.incidence_pct, 0)}%`) },
        { key: 'trees_affected', label: 'Trees', align: 'right', render: (row) => formatNumber(row.trees_affected, 0) },
        { key: 'action_taken', label: 'Action', render: (row) => (
          <span className="cell-stack">
            <strong>{optionLabel(PEST_ACTIONS, row.action_taken)}</strong>
            <small className="muted">{row.treatment_product ?? ''}</small>
          </span>
        ) },
        { key: 'follow_up_on', label: 'Follow up', render: (row) => (
          !row.follow_up_on ? <span className="muted">--</span>
            : overdue(row) ? <span className="field-error">{formatDate(row.follow_up_on)}</span>
              : formatDate(row.follow_up_on)
        ) },
        { key: 'record_status', label: 'Status', render: (row) => <StatusPill value={row.record_status} /> },
      ]}
      stats={[
        { key: 'open', label: 'Open', tone: 'warn', match: (row) => row.record_status === 'open' },
        { key: 'severe', label: 'High or severe', tone: 'error',
          match: (row) => ['high', 'severe'].includes(row.severity) && row.record_status !== 'resolved' },
        { key: 'overdue', label: 'Follow-up due', tone: 'error', match: overdue },
        { key: 'untreated', label: 'No action yet', tone: 'info',
          match: (row) => row.action_taken === 'none' && row.record_status === 'open' },
        { key: 'resolved', label: 'Resolved', tone: 'ok', match: (row) => row.record_status === 'resolved' },
      ]}
      defaults={{ observed_on: todayIso(), action_taken: 'monitoring', record_status: 'open', severity: 'low' }}
      fields={[
        plotField(pick.plots),
        speciesField(pick, true),
        { key: 'observed_on', label: 'Observed on', type: 'date', required: true, max: todayIso() },
        { key: 'issue_type', label: 'Type', type: 'select', options: ISSUE_TYPES, required: true },
        { key: 'issue_name', label: 'Pest / disease name', type: 'text', required: true, suggestions: SAMPLES.pestDisease },
        { key: 'plant_part', label: 'Part affected', type: 'select', options: PLANT_PARTS },
        { key: 'severity', label: 'Severity', section: 'Severity', type: 'select', options: SEVERITIES, required: true },
        { key: 'incidence_pct', label: 'Incidence (% of trees)', type: 'number' },
        { key: 'trees_affected', label: 'Trees affected', type: 'number' },
        { key: 'symptoms', label: 'Symptoms', type: 'textarea' },
        { key: 'action_taken', label: 'Action taken', section: 'Action', type: 'select', options: PEST_ACTIONS, required: true },
        // The treatment has to be an input recorded on THIS plot: a spray on a neighbouring plot
        // is not evidence that this one was treated.
        { key: 'input_application_id', label: 'Treatment applied', type: 'select',
          optionsFor: (form) => (inputs.data ?? [])
            .filter((row) => row.plot_id === form.plot_id)
            .map((row) => ({ id: row.id, label: `${row.product_name} (${formatDate(row.applied_on)})` })),
          placeholderFor: (form) => (!form.plot_id ? 'Choose the plot first'
            : (inputs.data ?? []).some((row) => row.plot_id === form.plot_id)
              ? 'Select the input record' : 'No inputs recorded on this plot yet'),
          showIf: (form) => ['chemical_control', 'biological_control'].includes(String(form.action_taken)) },
        { key: 'follow_up_on', label: 'Follow up on', type: 'date' },
        { key: 'record_status', label: 'Status', type: 'select', options: PEST_STATUS, required: true },
        { key: 'photo_document_id', label: 'Photo', section: 'Evidence', type: 'file', accept: 'image/*' },
        { key: 'remarks', label: 'Remarks', type: 'textarea' },
      ]}
      validate={(form) => ({
        species_inventory_id: speciesBelongsToPlot(pick, form),
        observed_on: dateNotFuture(form, 'observed_on', 'The date'),
        incidence_pct: form.incidence_pct && (Number(form.incidence_pct) < 0 || Number(form.incidence_pct) > 100) ? 'Between 0 and 100.' : undefined,
        follow_up_on: form.follow_up_on && String(form.follow_up_on) < String(form.observed_on ?? '') ? 'Follow-up cannot be before the observation.' : undefined,
      })}
    />
  );
}
